/**
 * AgentResponseQueuePage
 *
 * Manager UI to review, approve, reject, or replace agent-drafted messages held
 * pending human review (BR-REVIEW-HOLD, BR-RESOLVE-APPROVE, BR-RESOLVE-REJECT,
 * BR-RESOLVE-REPLACE, BR-QUEUE-AUTHZ).
 *
 * Route: /agent/review-queue
 *
 * Pattern: modelled on ExpenseReviewQueuePage (BR-REVIEW-HOLD / BR-RESOLVE-*).
 *
 * Response types are derived from the boundary contract in `@resida/api-types/agent`
 * — the same Zod schemas apps/api registers on these routes — so any field-name drift
 * is a typecheck error rather than a runtime `undefined`.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AgentUpgradePrompt } from '../components/billing/AgentUpgradePrompt';
import { PageHeader, EmptyState } from '../components/layout/index';
import { useBillingFeatures } from '../hooks/useBilling';
import { useDomainEvents } from '../hooks/useDomainEvents';
import { useUnseenCounts, useMarkSeen } from '../hooks/useAwareness';
import { AGENT_REVIEW_QUEUE_PENDING_COUNT_KEY } from '../hooks/useAgentReviewQueueCount';
import type { QueueItem, QueueListResponse } from '@resida/api-types/agent';
import { X } from 'lucide-react';
import { useImprovementConversationOpener } from '../components/agent/improvement/ImprovementConversationProvider';
import { playbookAgentKindSchema } from '@resida/api-types/agent';
import { WhyAgentStoppedNote } from '../components/agent/WhyAgentStoppedNote';
import { referenceCheckLeadPath } from '../lib/reference-check-links';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function agentQueueFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/landlord/api${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    const error = new Error(message) as Error & { status: number };
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function fetchAgentQueue(): Promise<QueueListResponse> {
  return agentQueueFetch<QueueListResponse>('/agent-response-queue?status=pending_review');
}

async function approveQueueItem(id: string): Promise<void> {
  return agentQueueFetch<void>(`/agent-response-queue/${encodeURIComponent(id)}/approve`, { method: 'POST' });
}

async function rejectQueueItem(id: string): Promise<void> {
  return agentQueueFetch<void>(`/agent-response-queue/${encodeURIComponent(id)}/reject`, { method: 'POST' });
}

async function replaceQueueItem(id: string, replacementContent: string): Promise<void> {
  return agentQueueFetch<void>(
    `/agent-response-queue/${encodeURIComponent(id)}/replace`,
    { method: 'POST', body: JSON.stringify({ replacementContent }) }
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(ageMs: number): string {
  const totalSeconds = Math.floor(ageMs / 1000);
  if (totalSeconds < 60) return 'just now';
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m ago`;
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) return `${totalHours}h ago`;
  const totalDays = Math.floor(totalHours / 24);
  return `${totalDays}d ago`;
}

function getAgeBadgeVariant(ageMs: number): 'default' | 'secondary' | 'destructive' {
  const hours = ageMs / 3_600_000;
  if (hours >= 24) return 'destructive';
  if (hours >= 4) return 'default';
  return 'secondary';
}

function formatRecipientType(recipientType: string): string {
  return recipientType.charAt(0).toUpperCase() + recipientType.slice(1);
}

/**
 * RA-QUEUE-LINK-FIX — maps a queue item's agentKindSlug to its own kind's detail route.
 * Returns null for an unrecognized slug so the caller can fall back to the plain-text span
 * rather than link to a route that doesn't exist for that kind.
 *
 * BR-ESC-QUEUE-REACH: a reference-check case has no page of its own — its work is on the
 * originating lead's Verifications tab, reached through the item's `leadId` (null → plain text).
 *
 * DEVIATION FROM THE COMPILED PLAN'S LITERAL SNIPPET (flagged per <constraints> — see notes):
 * the plan text checked `agentKindSlug === 'lead' || agentKindSlug === 'listing'`, but the only
 * AgentKind rows this codebase actually registers are slug 'repair' and slug 'leasing' — the
 * leasing/leads case and the listing case are both handled by the SAME 'leasing' AgentKind
 * (confirmed at apps/api/src/services/agent/agent-spicedb.service.ts:128 and
 * apps/api/src/test-auth/test-seed.routes.ts:2420/2525, both of which query
 * `agentKind.findUnique({ where: { slug: 'repair' | 'leasing' } })`). Implementing the plan's
 * literal 'lead'/'listing' checks would never match a real queue item's agentKindSlug, so every
 * lead/listing hold would silently fall through to the plain-text-span fallback — the exact bug
 * RA-QUEUE-LINK-FIX exists to fix. Checking 'leasing' instead is the same one-line, same-file
 * fix the plan specifies, against the real slug value.
 */
function getCaseDetailPath(agentKindSlug: string, caseId: string, leadId?: string | null): string | null {
  if (agentKindSlug === 'repair') return `/repairs/${caseId}`;
  if (agentKindSlug === 'leasing') return `/leasing/leads/${caseId}`;
  if (agentKindSlug === 'reference_coordinator' && leadId) return referenceCheckLeadPath(leadId, caseId);
  return null;
}

// ---------------------------------------------------------------------------
// Replace editor (per-item inline)
// ---------------------------------------------------------------------------

interface ReplaceEditorProps {
  item: QueueItem;
  onReplaced: () => void;
  onCancel: () => void;
}

function ReplaceEditor({ item, onReplaced, onCancel }: ReplaceEditorProps) {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await replaceQueueItem(item.id, content.trim());
      onReplaced();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to replace message');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mt-4 space-y-2"
      data-testid={`replace-editor-${item.id}`}
    >
      <label
        htmlFor={`replace-content-${item.id}`}
        className="block text-xs font-medium text-muted-foreground"
      >
        Your replacement message (sent to {formatRecipientType(item.recipientType)} instead)
      </label>
      <textarea
        id={`replace-content-${item.id}`}
        data-testid={`replace-content-input-${item.id}`}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={4}
        maxLength={4000}
        placeholder="Write your message here…"
        className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y"
        aria-label="Replacement message content"
        required
      />
      <p className="text-xs text-muted-foreground text-right">
        {content.length}/4000
      </p>
      {error && (
        <div
          role="alert"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
        >
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          className="min-h-[44px]"
          disabled={saving || !content.trim()}
          data-testid={`replace-submit-${item.id}`}
          aria-label="Send replacement message"
        >
          {saving ? 'Sending…' : 'Send replacement'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          onClick={onCancel}
          disabled={saving}
          data-testid={`replace-cancel-${item.id}`}
          aria-label="Cancel replacement"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Queue item row
// ---------------------------------------------------------------------------

interface QueueItemRowProps {
  item: QueueItem;
  onResolved: (id: string, action: 'approved' | 'rejected' | 'replaced') => void;
  /** DC-4/BR-16 — un-cleared awareness item count for THIS row's instance (0 = no mark). */
  unseenCount: number;
}

function QueueItemRow({ item, onResolved, unseenCount }: QueueItemRowProps) {
  const [action, setAction] = useState<'approve' | 'reject' | 'replace' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      await approveQueueItem(item.id);
      onResolved(item.id, 'approved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    setError(null);
    try {
      await rejectQueueItem(item.id);
      onResolved(item.id, 'rejected');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
      setBusy(false);
    }
  };

  const handleReplaced = () => {
    onResolved(item.id, 'replaced');
  };

  const ageBadgeVariant = getAgeBadgeVariant(item.ageMs);

  return (
    <div
      data-testid={`queue-item-${item.id}`}
      className="rounded-lg border border-border bg-card p-4"
      role="listitem"
    >
      {/* Row header */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex flex-wrap items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium text-foreground">
            Agent message for{' '}
            <span
              data-testid={`queue-item-recipient-${item.id}`}
              className="font-semibold"
            >
              {formatRecipientType(item.recipientType)}
            </span>
          </span>
          <Badge
            variant={ageBadgeVariant}
            data-testid={`queue-item-age-${item.id}`}
            aria-label={`Waiting ${formatAge(item.ageMs)}`}
          >
            {formatAge(item.ageMs)}
          </Badge>
          {unseenCount > 0 && (
            <span
              data-testid={`queue-item-unseen-${item.id}`}
              aria-label={`${unseenCount} unseen update${unseenCount === 1 ? '' : 's'}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
              {unseenCount > 1 ? `${unseenCount > 99 ? '99+' : unseenCount} new` : 'New'}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {(() => {
            const caseDetailPath = getCaseDetailPath(item.agentKindSlug, item.caseId, item.leadId);
            // A reference check has no page of its own, so its link names where it goes ("Open
            // lead"), as the escalation row does, instead of showing the case's internal id.
            const isReferenceCheck = item.agentKindSlug === 'reference_coordinator';
            return caseDetailPath ? (
              <Link
                to={caseDetailPath}
                data-testid={`queue-item-case-${item.id}`}
                title={isReferenceCheck ? undefined : `Case: ${item.caseId}`}
                className="inline-flex min-h-[44px] items-center underline underline-offset-2 hover:text-foreground"
              >
                {isReferenceCheck ? 'Open lead' : `Case: ${item.caseId.slice(0, 8)}…`}
              </Link>
            ) : (
              <span
                data-testid={`queue-item-case-${item.id}`}
                title={`Case: ${item.caseId}`}
              >
                Case: {item.caseId.slice(0, 8)}…
              </span>
            );
          })()}
        </div>
      </div>

      {item.forcedByReviewer && item.reviewerCritique && item.reviewerTier && (
        <WhyAgentStoppedNote
          data-testid={`queue-item-why-stopped-${item.id}`}
          critique={item.reviewerCritique}
          tier={item.reviewerTier}
          itemText={item.reviewerItemText}
        />
      )}

      {/* Proposed content */}
      <div
        data-testid={`queue-item-content-${item.id}`}
        className="mb-4 p-3 rounded-md bg-muted/60 text-sm text-foreground whitespace-pre-wrap border border-border"
        aria-label="Agent-proposed message content"
      >
        {item.proposedContent}
      </div>

      {/* Error */}
      {error && !action && (
        <div
          role="alert"
          className="mb-3 p-2 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-xs"
        >
          {error}
        </div>
      )}

      {/* Replace editor */}
      {action === 'replace' && (
        <ReplaceEditor
          item={item}
          onReplaced={handleReplaced}
          onCancel={() => { setAction(null); setError(null); }}
        />
      )}

      {/* Reject confirmation */}
      {action === 'reject' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground">
            Reject this draft? The message will be suppressed and not delivered.
          </span>
          {error && (
            <span role="alert" className="text-xs text-destructive">{error}</span>
          )}
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="min-h-[44px]"
            onClick={() => void handleReject()}
            disabled={busy}
            data-testid={`queue-item-reject-confirm-${item.id}`}
            aria-label="Confirm rejection of this agent draft"
          >
            {busy ? 'Rejecting…' : 'Confirm reject'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            onClick={() => { setAction(null); setError(null); }}
            disabled={busy}
            data-testid={`queue-item-reject-cancel-${item.id}`}
            aria-label="Cancel rejection"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Action buttons (shown when no action is selected) */}
      {action === null && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="min-h-[44px]"
            onClick={() => void handleApprove()}
            disabled={busy}
            data-testid={`queue-item-approve-${item.id}`}
            aria-label="Approve this agent draft for delivery"
          >
            {busy ? 'Approving…' : 'Approve'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAction('reject')}
            disabled={busy}
            data-testid={`queue-item-reject-${item.id}`}
            aria-label="Reject this agent draft"
            className="min-h-[44px] text-destructive hover:text-destructive border-destructive/30 hover:border-destructive"
          >
            Reject
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            onClick={() => setAction('replace')}
            disabled={busy}
            data-testid={`queue-item-replace-${item.id}`}
            aria-label="Replace this agent draft with your own message"
          >
            Replace with my own
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Tell the agent why" — the prompt after a reject / replace has SUCCEEDED (IMP-BR-6 entry 4,
// BR-AGENT-FB-3). Non-blocking: the resolution is already done and is never reverted by anything
// that happens here (a failed start only adds a notice). Approve has no affordance.
// ---------------------------------------------------------------------------

type FeedbackPrompt = { item: QueueItem; action: 'rejected' | 'replaced' };

const TELL_WHY_COPY: Record<FeedbackPrompt['action'], { pill: string; prompt: string; subtitle: string }> = {
  rejected: {
    pill: 'Rejected — not sent',
    prompt: 'Rejected. Want the agent to do it your way next time?',
    subtitle: 'About a draft you rejected',
  },
  replaced: {
    pill: 'Replaced — your reply was sent',
    prompt: 'Sent your version. Want the agent to do it your way next time?',
    subtitle: 'About a draft you replaced',
  },
};

function TellWhyRow({ prompt, onDismiss }: { prompt: FeedbackPrompt; onDismiss: () => void }) {
  const { item, action } = prompt;
  const openConversation = useImprovementConversationOpener();
  const [failedNotice, setFailedNotice] = useState<string | null>(null);
  const copy = TELL_WHY_COPY[action];
  const agentKind = playbookAgentKindSchema.safeParse(item.agentKindSlug);

  const tellTheAgentWhy = () => {
    setFailedNotice(null);
    openConversation({
      start: { entry: 'tell_us_why', anchor: { kind: 'draft', reviewLogId: item.id }, caseId: item.caseId },
      agentKind: agentKind.success ? agentKind.data : undefined,
      subtitle: copy.subtitle,
      anchorCard: { label: 'The agent drafted', text: item.proposedContent },
      onStartFailed: (kind) =>
        setFailedNotice(
          kind === 'rate_limited'
            ? "You've sent a lot of messages in the last hour. Your draft is still resolved — try again in a little while."
            : "We couldn't reach the agent. Your draft is still resolved — you can try again or dismiss this.",
        ),
    });
  };

  return (
    <div
      data-testid={`agent-queue-resolved-${item.id}`}
      className="rounded-lg border border-border bg-card p-4"
      role="listitem"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium capitalize text-foreground">{item.recipientType}</span>
        <Badge variant="secondary" className="ml-auto">{copy.pill}</Badge>
      </div>
      <div className="mb-3 rounded-md border border-border bg-muted/60 p-3 text-sm whitespace-pre-wrap text-muted-foreground line-through">
        {item.proposedContent}
      </div>
      <div
        role="status"
        className="flex flex-col gap-2 rounded-lg border border-primary/25 bg-primary/10 p-3 text-sm text-foreground sm:flex-row sm:flex-wrap sm:items-center"
      >
        <span className="min-w-0 sm:flex-1">{copy.prompt}</span>
        <div className="flex items-center gap-2">
          <Button type="button" className="font-semibold" onClick={tellTheAgentWhy} data-testid={`agent-queue-tell-why-${item.id}`}>
            Tell the agent why
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            onClick={onDismiss}
            aria-label="Dismiss"
            data-testid={`agent-queue-tell-why-dismiss-${item.id}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {failedNotice && (
        <p role="status" className="m-0 mt-2 text-sm text-muted-foreground" data-testid={`agent-queue-feedback-failed-${item.id}`}>
          {failedNotice}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function AgentResponseQueuePage() {
  const { data: featuresData } = useBillingFeatures();
  const hasAgenticSupport = featuresData?.features.includes('agentic_support') ?? false;
  const queryClient = useQueryClient();

  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [feedbackPrompts, setFeedbackPrompts] = useState<FeedbackPrompt[]>([]);

  // Extracted so it can be called from both the initial useEffect and the Retry button (CF-2).
  const loadQueue = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const data = await fetchAgentQueue();
      setItems(data.items);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasAgenticSupport) {
      setLoading(false);
      return;
    }
    void loadQueue();
  }, [loadQueue, hasAgenticSupport]);

  // DC-2 — reactive on agent.draft_held: a new hold means the list is stale.
  useDomainEvents({
    'agent.draft_held': useCallback(() => {
      void loadQueue();
    }, [loadQueue]),
  });

  // DC-4/BR-16 — per-row unseen marks, keyed by this row's own AgentResponseReviewLog id
  // (the awareness instance's referenceId for the agent_draft_held_for_review class).
  const { data: unseenCountsData } = useUnseenCounts();
  const unseenByReviewLogId = useMemo(() => new Map(
    (unseenCountsData?.byInstance ?? [])
      .filter((entry) => entry.referenceType === 'agent_response_review_log')
      .map((entry) => [entry.referenceId, entry.count])
  ), [unseenCountsData]);

  // BR-16 — this list page IS the review surface for these holds (there is no per-item
  // detail route: notification-links.test.ts documents agent_response_review_log as
  // "list page, no per-review route exists"). Without a mark-seen call somewhere, these
  // counts could never clear — unlike LeadInboxPage, which deliberately leaves marking to
  // the lead detail page it links to. Mark each currently-unseen row seen once it's
  // rendered here; markedRef guards against re-firing on every unrelated re-render/refetch.
  const markSeen = useMarkSeen();
  const markedRef = useRef(new Set<string>());
  useEffect(() => {
    for (const [id, count] of unseenByReviewLogId) {
      if (count > 0 && !markedRef.current.has(id)) {
        markedRef.current.add(id);
        markSeen.mutate({ referenceType: 'agent_response_review_log', referenceId: id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenByReviewLogId]);

  const handleResolved = (id: string, action: 'approved' | 'rejected' | 'replaced') => {
    const resolvedItem = items.find((item) => item.id === id);
    if (resolvedItem && action !== 'approved') {
      setFeedbackPrompts((prev) => [...prev, { item: resolvedItem, action }]);
    }
    setItems((prev) => prev.filter((item) => item.id !== id));
    const label = action === 'approved' ? 'Approved' : action === 'rejected' ? 'Rejected' : 'Replaced';
    setSuccessMessage(`${label} — message ${action === 'approved' ? 'queued for delivery' : action === 'rejected' ? 'suppressed' : 'sent with your content'}.`);
    setTimeout(() => setSuccessMessage(null), 4000);
    // Notify the Sidebar's pending-count badge to refresh (BR-7 — same query-invalidation
    // path the domain-event bus uses; no more window CustomEvent side channel).
    void queryClient.invalidateQueries({ queryKey: AGENT_REVIEW_QUEUE_PENDING_COUNT_KEY });
  };

  // UF-5: gate on agentic_support — featuresData is undefined while loading, so wait
  // for it before rendering (avoids flash of the not-enabled state).
  if (!featuresData) {
    return (
      <div data-testid="agent-queue-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-80 mt-2" />
        </div>
      </div>
    );
  }

  if (!hasAgenticSupport) {
    return (
      <div data-testid="agent-queue-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          {/* AC-L18: PageHeader's `subtitle` prop renders inside a `truncate` <p>, which
              mid-word-truncates this sentence on mobile instead of wrapping. Render the
              subtitle manually (no truncate) rather than editing the shared PageHeader,
              which other pages also rely on truncating single-line subtitles. */}
          <PageHeader
            title="Agent Review Queue"
            backLink={{ to: '/dashboard', label: 'Dashboard' }}
          />
          <p className="-mt-2 mb-4 text-sm text-muted-foreground" data-testid="agent-queue-subtitle">
            Review and resolve agent-drafted messages pending your approval
          </p>
        </div>
        <div className="px-[var(--page-padding-x)]">
          <AgentUpgradePrompt title="Turn on agent support to review agent messages" />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        data-testid="agent-queue-page"
        className="max-w-4xl mx-auto pb-6"
      >
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-80 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="agent-queue-page"
      className="max-w-4xl mx-auto pb-6"
    >
      {/* Header. AC-L18: subtitle rendered manually (see EmptyState branch above for why). */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Agent Review Queue"
          backLink={{ to: '/dashboard', label: 'Dashboard' }}
        />
        <p className="-mt-2 mb-4 text-sm text-muted-foreground" data-testid="agent-queue-subtitle">
          Review and resolve agent-drafted messages pending your approval
        </p>
      </div>

      <div className="px-[var(--page-padding-x)] mb-4">
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Agent-authored messages held for your review appear here. Approve to deliver as-is,
          reject to suppress the message, or replace it with your own content. Approve in
          draft order — the oldest holds appear first.
        </div>
      </div>

      {/* Page error */}
      {pageError && (
        <div
          role="alert"
          data-testid="agent-queue-error"
          className="mx-[var(--page-padding-x)] mb-4 p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
        >
          <p>{pageError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 min-h-[44px]"
            data-testid="agent-queue-retry"
            onClick={() => void loadQueue()}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div
          role="status"
          aria-live="polite"
          data-testid="agent-queue-success"
          className="mx-[var(--page-padding-x)] mb-4 p-3 bg-success/10 border border-success/25 rounded-md text-success text-sm"
        >
          {successMessage}
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && feedbackPrompts.length === 0 && !pageError && (
        <div
          data-testid="agent-queue-empty"
          className="px-[var(--page-padding-x)]"
        >
          <EmptyState
            title="No messages pending review"
            description="Agent drafts waiting for your approval will appear here. When an agent composes a reply, it is held here before delivery."
            headingLevel="h2"
            action={
              <Button asChild variant="outline">
                <Link to="/dashboard">Back to Dashboard</Link>
              </Button>
            }
          />
        </div>
      )}

      {/* Resolved reject/replace rows with their optional "Tell us why" prompt */}
      {feedbackPrompts.length > 0 && (
        <div
          role="list"
          aria-label="Resolved agent drafts"
          className="px-[var(--page-padding-x)] mb-4 flex flex-col gap-4"
          data-testid="agent-queue-resolved-list"
        >
          {feedbackPrompts.map((prompt) => (
            <TellWhyRow
              key={prompt.item.id}
              prompt={prompt}
              onDismiss={() => setFeedbackPrompts((prev) => prev.filter((p) => p.item.id !== prompt.item.id))}
            />
          ))}
        </div>
      )}

      {/* Queue items */}
      {items.length > 0 && (
        <div
          role="list"
          aria-label="Agent response review queue"
          className="px-[var(--page-padding-x)] flex flex-col gap-4"
          data-testid="agent-queue-list"
        >
          {items.map((item) => (
            <QueueItemRow
              key={item.id}
              item={item}
              onResolved={handleResolved}
              unseenCount={unseenByReviewLogId.get(item.id) ?? 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AgentResponseQueuePage;
